import { Injectable, inject, DestroyRef, NgZone, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { timer, Subscription, Subject, catchError, of, retry, throwError } from 'rxjs';
import { MessageService } from '@openng/optimus-ui/api';
import { LiveAssistantService } from './live-assistant.service';
import { ElectronService } from '../../../core/services/electron.service';
import { environment } from '../../../../environments/environment';
import { ActiveMeetingResponse, AutoStartReadyPayload, AutoStopPromptPayload } from '../models/live-assistant-auto-start.interface';
import { LiveAssistantSessionStartResponse } from '../models/live-assistant-session.types';
import { Router } from '@angular/router';

// The machine reports waking before Wi-Fi has rejoined, so the first read after a resume usually
// cannot reach the API at all. Give the network a few chances to come back before giving up.
const WAKE_FETCH_RETRIES = 3;
const WAKE_FETCH_RETRY_DELAY_MS = 5_000;

export interface AssistantCommand {
  action: 'start';
  payload: LiveAssistantSessionStartResponse;
}

@Injectable({ providedIn: 'root' })
export class LiveAssistantAutoStartService {
  private readonly electronService = inject(ElectronService);
  private readonly liveAssistantService = inject(LiveAssistantService);
  private readonly messageService = inject(MessageService);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly apiUrl = environment.apiUrl;
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);
  public readonly assistantCommands$ = new Subject<AssistantCommand>();
  public readonly pendingAutoStop = signal<{ calendarEventId: string; meetingTitle: string } | null>(null);
  private wrapUpTimerSub?: Subscription;
  private wakeCheckSub?: Subscription;
  private readonly syncChannel = new BroadcastChannel('live_assistant_autostart_sync');

  constructor() {
    this.listenToSystemWakeUp();
    this.listenToNotificationActions();
    this.syncChannel.onmessage = event => {
      this.ngZone.run(() => {
        if (event.data?.type === 'SYNC_PENDING_AUTO_STOP') {
          this.pendingAutoStop.set(event.data.payload);
          if (!event.data.payload) {
            this.wrapUpTimerSub?.unsubscribe();
          }
        }
      });
    };

    this.destroyRef.onDestroy(() => {
      this.syncChannel.close();
    });
  }

  private listenToNotificationActions(): void {
    if (!this.electronService.isElectron) return;

    const unsubscribe = this.electronService.onNotificationAction(actionId => {
      this.ngZone.run(() => {
        if (actionId === 'STOP_SESSION') {
          this.manualWrapUp();
        } else if (actionId.startsWith('EXTEND_MEETING_')) {
          const calendarEventId = actionId.replace('EXTEND_MEETING_', '');
          this.extendMeeting(calendarEventId, 15);
        }
      });
    });

    this.destroyRef.onDestroy(unsubscribe);
  }

  public handleAutoStartReady(payload: AutoStartReadyPayload): void {
    if (!this.electronService.isElectron) return;

    // 2. Wrap the entire flow in NgZone so Angular knows to redraw the screen
    this.ngZone.run(() => {
      if (this.liveAssistantService.sessionState() === 'active') {
        this.messageService.add({
          severity: 'info',
          summary: 'Back-to-back meeting',
          detail: `Your next meeting "${payload.meetingTitle}" is starting. Wrap up your current session to join.`,
          life: 10000,
        });
        return;
      }

      this.electronService.showNativeNotification('Live Assistant Starting', `Preparing context for: ${payload.meetingTitle}`);

      const sessionResponse: LiveAssistantSessionStartResponse = {
        sessionId: payload.sessionId,
        realtimeConnectionConfig: payload.realtimeConnectionConfig,
        microphoneClientSecret: payload.microphoneClientSecret,
        tabAudioClientSecret: payload.tabAudioClientSecret,
        audioRecordingEnabled: true,
      };

      this.router.navigate(['/main/assistant']).then(success => {
        if (!success) console.error('Navigation to /main/assistant failed.');
        this.assistantCommands$.next({ action: 'start', payload: sessionResponse });
      });
    });
  }

  public handleAutoStopPrompt(payload: AutoStopPromptPayload): void {
    // 3. Wrap in NgZone so the toast appears instantly
    this.ngZone.run(() => {
      const promptState = { calendarEventId: payload.calendarEventId, meetingTitle: payload.meetingTitle };
      this.pendingAutoStop.set(promptState);
      this.syncChannel.postMessage({ type: 'SYNC_PENDING_AUTO_STOP', payload: promptState });
      const msg = `Meeting '${payload.meetingTitle}' ended. Wrap up assistant?`;

      if (this.electronService.isElectron) {
        this.electronService.showNativeNotification('Meeting Ended', msg, [
          { id: 'STOP_SESSION', text: 'Stop Session' },
          { id: `EXTEND_MEETING_${payload.calendarEventId}`, text: 'Add +15 Min' },
        ]);
      }

      this.messageService.add({
        severity: 'warn',
        summary: 'Meeting Ended',
        detail: msg,
        sticky: true,
        id: 'auto-stop-toast',
      });

      this.wrapUpTimerSub?.unsubscribe();
      const gracePeriodMs = 10 * 60 * 1000;
      this.wrapUpTimerSub = timer(gracePeriodMs).subscribe(() => {
        this.manualWrapUp();
      });
    });
  }

  public extendMeeting(calendarEventId: string, minutes = 15): void {
    this.pendingAutoStop.set(null);
    this.syncChannel.postMessage({ type: 'SYNC_PENDING_AUTO_STOP', payload: null });
    this.wrapUpTimerSub?.unsubscribe();
    this.http.post(`${this.apiUrl}/meeting-reminders/${calendarEventId}/extend`, { minutes }, { withCredentials: true }).subscribe({
      next: () => {
        this.ngZone.run(() => {
          this.messageService.add({ severity: 'success', summary: 'Meeting Extended', detail: `Assistant will run for ${minutes} more minutes.` });
        });
      },
      error: () => {
        this.ngZone.run(() => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to extend meeting.' });
        });
      },
    });
  }

  public manualWrapUp(): void {
    this.pendingAutoStop.set(null);
    this.syncChannel.postMessage({ type: 'SYNC_PENDING_AUTO_STOP', payload: null });
    this.wrapUpTimerSub?.unsubscribe();

    this.ngZone.run(() => {
      this.liveAssistantService.requestStopSession();
    });
  }

  private listenToSystemWakeUp(): void {
    if (!this.electronService.isElectron) return;

    const unsubscribe = this.electronService.onSystemWakeUp(() => {
      // Waking again while a check is still retrying (lid closed and reopened) would otherwise show the
      // same toast twice.
      this.wakeCheckSub?.unsubscribe();
      this.wakeCheckSub = this.http
        .get<ActiveMeetingResponse[]>(`${this.apiUrl}/meeting-reminders/active-meetings`, { withCredentials: true })
        .pipe(
          // Only a request that never reached the server (status 0) is worth repeating; an answer
          // from the API will not change by asking again.
          retry({
            count: WAKE_FETCH_RETRIES,
            delay: error => (error instanceof HttpErrorResponse && error.status === 0 ? timer(WAKE_FETCH_RETRY_DELAY_MS) : throwError(() => error)),
          }),
          // The toast is a hint, not something the rep asked for: a read that still fails is dropped
          // instead of reaching the global error handler on every laptop wake.
          catchError(() => of([] as ActiveMeetingResponse[])),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(reminders => {
          const now = Date.now();
          const activeMeeting = reminders.find(r => new Date(r.start).getTime() < now && new Date(r.end).getTime() > now);

          if (activeMeeting && this.liveAssistantService.sessionState() !== 'active') {
            this.messageService.add({
              severity: 'info',
              summary: 'Active Meeting Detected',
              detail: `You have an active meeting (${activeMeeting.title}). Click to launch Live Assistant.`,
              life: 15000,
            });
          }
        });
    });

    this.destroyRef.onDestroy(unsubscribe);
  }
}
